






























































import { computed, defineComponent, ref, PropType } from '@vue/composition-api';
import MusicIcon from './music-icon.vue';
import MusicController from './music-play-progress-controller.vue';
import _ from 'lodash-es';

type MusicInfo = {
  author: string;
  cover: string;
  duration: number;
  musicId: string;
  playUrl: string;
  title: string;
};

export default defineComponent({
  components: {
    MusicIcon,
    MusicController,
  },
  props: {
    musicInfo: {
      type: Object as PropType<MusicInfo>,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, ctx) {
    const player = ref();
    const SECOND_1 = 1000;
    const PERCENT = 100;
    const getPercentNum = (num: number) => num / PERCENT;
    const getNumberLimitMinAndMax = (target: number, [min, max]: [min: number, max: number]) => {
      if (typeof target === 'number') {
        return Math.max(min, Math.min(target, max));
      }
      return 0;
    };
    const formatNumDisplay = (num: number) => (num < 10 ? `0${num}` : num);
    const formatTime = (sec: number) => {
      const time = typeof sec === 'number' ? sec : 0;
      const minuteNum = formatNumDisplay(Math.floor(time / 60));
      const secNum = formatNumDisplay(Math.floor(time % 60));

      return `${minuteNum}: ${secNum}`;
    };

    const musicPlayer = {
      ready: ref(false),
      onPlaying: ref(false),
      playProgress: ref(0),
      musicDuration: ref(0),
      play: () => {
        musicPlayer.onPlaying.value = true;
        player.value?.play();
      },
      pause: () => {
        musicPlayer.onPlaying.value = false;
        player.value?.pause();
      },
      getMusicDuration: () => player.value?.duration || 0,
      getCurrentTime: () => player.value?.currentTime || 0,
      setCurrentTime: (time: number) => {
        if (player.value && musicPlayer.ready.value) {
          const maxTime = musicPlayer.musicDuration.value;
          const targetTime = getNumberLimitMinAndMax(time, [0, maxTime]);

          player.value.currentTime = targetTime;
        }
      },
    };

    const progressDisplay = computed({
      get: () => getNumberLimitMinAndMax(musicPlayer.playProgress.value, [0, 100]) || 0,
      set: _.debounce((progress: number) => {
        if (musicPlayer.ready) {
          const targetTime = Math.floor(musicPlayer.musicDuration.value * getPercentNum(progress)) || 0;

          musicPlayer.setCurrentTime(targetTime);
        }
      }, 100),
    });

    const allTimeDisplay = computed(() => formatTime(musicPlayer.musicDuration.value));

    const currentTimeDisplay = computed(() => {
      const { musicDuration, playProgress } = musicPlayer;

      const currentTime = getPercentNum(musicDuration.value * playProgress.value);

      return formatTime(currentTime);
    });
    const togglePlayStatus = () => {
      const { ready, onPlaying, pause, play } = musicPlayer;

      if (!ready.value) {
        return;
      }

      onPlaying.value ? pause() : play();
    };

    const onMusicLoaded = () => {
      const { ready, musicDuration, getMusicDuration } = musicPlayer;
      ready.value = true;
      musicDuration.value = getMusicDuration();
    };

    const playerInit = () => {
      const { playProgress, pause, ready } = musicPlayer;
      ready.value = false;
      playProgress.value = 0;
      pause();
    };

    const onMusicCurrentTimeChange = _.throttle(() => {
      const { getCurrentTime, musicDuration } = musicPlayer;

      musicPlayer.playProgress.value = (getCurrentTime() / musicDuration.value) * PERCENT;
    }, SECOND_1);

    const onPlayEnded = () => {
      musicPlayer.pause();
      musicPlayer.playProgress.value = 0;
    };

    const onDelete = () => {
      ctx.emit('delete');
    };

    const handleEdit = () => {
      ctx.emit('music-edit');
    };

    return {
      player,
      onDelete,
      playerInit,
      handleEdit,
      onPlayEnded,
      musicPlayer,
      onMusicLoaded,
      allTimeDisplay,
      progressDisplay,
      togglePlayStatus,
      currentTimeDisplay,
      onMusicCurrentTimeChange,
      onPlaying: musicPlayer.onPlaying,
      musicReady: musicPlayer.ready,
    };
  },
});
